import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DownloadIcon from "@mui/icons-material/Download";

import DataTable from "../../components/brand/DataTable";
import KpiTile from "../../components/brand/KpiTile";
import Panel from "../../components/brand/Panel";
import { useAppToast } from "../../components/brand/useAppToast";
import { exportAgedPayablesExcel, getAgedPayables, getPortfolioAgedPayables } from "../../api/financeApi";
import { downloadBlob, exportErrorMessage } from "../../utils/blobDownload";
import { getApiErrorMessage } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const DEFAULT_BUCKETS = [
  { key: "current", label: "Current" },
  { key: "days_30", label: "30" },
  { key: "days_60", label: "60" },
  { key: "days_90", label: "90" },
  { key: "older", label: "Older" },
];

const OLDEST_DUE_DESC = "-oldest_due_date";
const OLDEST_DUE_ASC = "oldest_due_date";

const money = (value) => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount)
    : "—";
};

const bucketsFrom = (data) =>
  Array.isArray(data?.buckets) && data.buckets.length > 0 ? data.buckets : DEFAULT_BUCKETS;

export default function AgedPayablesPanel({ propertyId }) {
  const portfolio = !propertyId;
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sort, setSort] = useState(OLDEST_DUE_DESC);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const { showToast, toastNode } = useAppToast();

  useEffect(() => {
    setPage(0);
    setReport(null);
  }, [propertyId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const request = portfolio
      ? getPortfolioAgedPayables(page + 1, { page_size: size, sort })
      : getAgedPayables(propertyId, page + 1, { page_size: size, sort });
    request
      .then(({ data }) => {
        if (cancelled) return;
        setReport(data);
        if (data?.page) setPage(data.page - 1);
      })
      .catch((err) => {
        if (!cancelled) {
          setReport(null);
          setError(getApiErrorMessage(err, "Failed to load aged payables."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [portfolio, propertyId, page, size, sort]);

  const buckets = bucketsFrom(report);
  const rows = report?.results || [];
  const totals = report?.totals;
  const descending = sort === OLDEST_DUE_DESC;
  const leadColumns = portfolio ? 4 : 3;

  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    setError("");
    try {
      const response = await exportAgedPayablesExcel(propertyId);
      downloadBlob(response, "aged-payables.xlsx", EXCEL_TYPE);
      showToast("Aged payables downloaded.");
    } catch (err) {
      setError(await exportErrorMessage(err, "Failed to export aged payables."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Panel
        eyebrow="Creditors"
        title="Aged payables"
        noBodyPadding={false}
        right={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={exportExcel}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
            <Button
              variant="outlined"
              startIcon={descending ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
              onClick={() => {
                setSort(descending ? OLDEST_DUE_ASC : OLDEST_DUE_DESC);
                setPage(0);
              }}
            >
              Oldest due
            </Button>
          </Box>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {portfolio
            ? "Supplier amounts still owed across all properties, grouped by how long they have been due"
            : "Supplier amounts still owed on this property, grouped by how long they have been due"}
          {report?.as_of ? ` as at ${formatDateUk(report.as_of)}` : ""}.
        </Typography>
        {totals && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            {buckets.map((bucket) => (
              <KpiTile key={bucket.key} label={bucket.label} value={money(totals[bucket.key])} />
            ))}
            <KpiTile label="Total" value={money(totals.total)} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <CircularProgress size={24} aria-label="Loading aged payables" />}
        <Box sx={{ overflowX: "auto" }}>
          <DataTable aria-label="Aged payables">
            <TableHead>
              <TableRow>
                {portfolio && <TableCell>Property</TableCell>}
                <TableCell>Supplier</TableCell>
                <TableCell>Oldest due</TableCell>
                {buckets.map((bucket) => (
                  <TableCell key={bucket.key} align="right">
                    {bucket.label}
                  </TableCell>
                ))}
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={portfolio ? `${row.property_id}-${row.supplier_id}` : row.supplier_id} hover>
                  {portfolio && (
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/finance?propertyId=${row.property_id}&tab=aged-payables`}
                      >
                        {row.property_name}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    <Link component={RouterLink} to={`/suppliers/${row.supplier_id}`}>
                      {row.supplier_name}
                    </Link>
                  </TableCell>
                  <TableCell>{row.oldest_due_date ? formatDateUk(row.oldest_due_date) : "—"}</TableCell>
                  {buckets.map((bucket) => (
                    <TableCell key={bucket.key} align="right">
                      {money(row[bucket.key])}
                    </TableCell>
                  ))}
                  <TableCell align="right">{money(row.total)}</TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={buckets.length + leadColumns}>
                    {portfolio
                      ? "There are no outstanding supplier amounts."
                      : "No supplier amounts are outstanding for this property."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </DataTable>
        </Box>
        <TablePagination
          component="div"
          count={report?.total || 0}
          page={page}
          rowsPerPage={size}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
        />
      </Panel>
      {toastNode}
    </Box>
  );
}
